import { type CSSProperties, type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'
import { AudioWaveform, Frown, Headphones, LockKeyhole, Meh, Music, Pause, Play, Puzzle, Radio, RefreshCw, Settings, SkipForward, SlidersHorizontal, Smile, Video, X, type LucideIcon } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import {
  createListeningSignal,
  getChannelCoverUrl,
  getAudioImageUrl,
  getCurrentListeningSignals,
  getPublicChannels,
  resolveMediaUrl,
  type ListeningSignal,
  type ListeningSignalType,
  type PublicChannel
} from '../api'
import { getChannelColorTheme } from '../channelColorThemes'
import { MavolyraLogo } from '../components/MavolyraLogo'
import { PublicVolumeControl } from '../components/PublicVolumeControl'
import { useToast } from '../components/ToastProvider'
import { Button } from '../components/ui'
import { useChannelAudioPlayer } from '../features/publicPlayback/useChannelAudioPlayer'
import { usePublicLiveStreamPlayer } from '../features/publicPlayback/usePublicLiveStreamPlayer'
import type { AuthState } from '../types/auth'
import styles from './PublicChannelsPage.module.css'

type PublicPlaybackMode = 'live' | 'personal'

type ListeningSignalAction = {
  icon: LucideIcon
  label: string
  signalType: ListeningSignalType
  tone: 'bad' | 'neutral' | 'good'
}

type ListeningSignalCategory = 'audio_quality' | 'track' | 'channel_fit'

type ListeningSignalBlock = {
  actions: ListeningSignalAction[]
  category: ListeningSignalCategory
  helper: string
  icon: LucideIcon
  title: string
}

const playbackModeStorageKey = 'mavolyra.publicPlaybackMode'
const liveWaveformBarCount = 32
const liveWaveformFrameMs = 1000 / 30
const liveWaveformLevelAttack = 0.58
const liveWaveformLevelRelease = 0.34
const liveWaveformFallbackLevels = [0.18, 0.32, 0.52, 0.28, 0.66, 0.4, 0.24, 0.58, 0.72, 0.34, 0.62, 0.46, 0.22, 0.56, 0.38, 0.68]
const liveWaveformMaxLevel = 0.72
const liveWaveformMinLevel = 0.12
const liveWaveformNoiseFloor = 5
const liveWaveformMinFrequencyBins = 24

const cx = (...classNames: Array<string | false | null | undefined>): string =>
  classNames
    .filter(Boolean)
    .map((className) => styles[className as string] ?? className)
    .join(' ')

type AudioAnalysis = {
  analyser: AnalyserNode
  context: AudioContext
  data: Uint8Array<ArrayBuffer>
}

type WindowWithWebkitAudioContext = Window & typeof globalThis & {
  webkitAudioContext?: typeof AudioContext
}

const audioAnalysisByElement = new WeakMap<HTMLAudioElement, AudioAnalysis>()

const listeningSignalBlocks: ListeningSignalBlock[] = [
  {
    actions: [
      { icon: Frown, label: 'Bad', signalType: 'bad_audio_quality', tone: 'bad' },
      { icon: Meh, label: 'Neutral', signalType: 'neutral_audio_quality', tone: 'neutral' },
      { icon: Smile, label: 'Good', signalType: 'good_audio_quality', tone: 'good' }
    ],
    category: 'audio_quality',
    helper: 'Technical clarity & fidelity',
    icon: AudioWaveform,
    title: 'Sound quality'
  },
  {
    actions: [
      { icon: Frown, label: 'Bad', signalType: 'boring_track', tone: 'bad' },
      { icon: Meh, label: 'Neutral', signalType: 'neutral_track', tone: 'neutral' },
      { icon: Smile, label: 'Good', signalType: 'great_track', tone: 'good' }
    ],
    category: 'track',
    helper: 'How this track lands for you',
    icon: Music,
    title: 'Musical value'
  },
  {
    actions: [
      { icon: Frown, label: 'Bad', signalType: 'poor_channel_fit', tone: 'bad' },
      { icon: Meh, label: 'Neutral', signalType: 'neutral_channel_fit', tone: 'neutral' },
      { icon: Smile, label: 'Good', signalType: 'great_channel_fit', tone: 'good' }
    ],
    category: 'channel_fit',
    helper: 'Match with this channel',
    icon: Puzzle,
    title: 'Channel fit'
  }
]

const emptyListeningSignalSelection: Record<ListeningSignalCategory, ListeningSignalType | null> = {
  audio_quality: null,
  channel_fit: null,
  track: null
}

const listeningSignalCategoryByType: Record<ListeningSignalType, ListeningSignalCategory> = {
  bad_audio_quality: 'audio_quality',
  neutral_audio_quality: 'audio_quality',
  good_audio_quality: 'audio_quality',
  boring_track: 'track',
  neutral_track: 'track',
  great_track: 'track',
  poor_channel_fit: 'channel_fit',
  neutral_channel_fit: 'channel_fit',
  great_channel_fit: 'channel_fit'
}

const buildListeningSignalSelection = (
  signals: ListeningSignal[]
): Record<ListeningSignalCategory, ListeningSignalType | null> => {
  const selection = { ...emptyListeningSignalSelection }

  for (const signal of signals) {
    selection[listeningSignalCategoryByType[signal.signal_type]] = signal.signal_type
  }

  return selection
}

const readStoredPlaybackMode = (): PublicPlaybackMode => {
  if (typeof window === 'undefined') {
    return 'live'
  }

  return window.localStorage.getItem(playbackModeStorageKey) === 'personal' ? 'personal' : 'live'
}

const formatPlaybackTime = (value: number): string => {
  if (!Number.isFinite(value) || value <= 0) {
    return '0:00'
  }

  const minutes = Math.floor(value / 60)
  const seconds = Math.floor(value % 60).toString().padStart(2, '0')

  return `${minutes}:${seconds}`
}

const getCssColor = (element: HTMLElement, property: string, fallback: string): string => {
  const value = window.getComputedStyle(element).getPropertyValue(property).trim()

  return value || fallback
}

const getAdaptiveFrequencyBinLimit = (data: Uint8Array<ArrayBuffer>): number => {
  for (let index = data.length - 1; index >= 1; index -= 1) {
    if ((data[index] ?? 0) > liveWaveformNoiseFloor) {
      return Math.min(data.length, Math.max(liveWaveformMinFrequencyBins, index + 4))
    }
  }

  return Math.min(data.length, liveWaveformMinFrequencyBins)
}

const getAudioAnalysis = (audio: HTMLAudioElement): AudioAnalysis | null => {
  const existing = audioAnalysisByElement.get(audio)

  if (existing) {
    return existing
  }

  const AudioContextCtor = window.AudioContext ?? (window as WindowWithWebkitAudioContext).webkitAudioContext

  if (!AudioContextCtor) {
    return null
  }

  try {
    const context = new AudioContextCtor()
    const source = context.createMediaElementSource(audio)
    const analyser = context.createAnalyser()

    analyser.fftSize = 256
    analyser.smoothingTimeConstant = 0.58
    source.connect(analyser)
    analyser.connect(context.destination)

    const analysis = {
      analyser,
      context,
      data: new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount))
    }

    audioAnalysisByElement.set(audio, analysis)

    return analysis
  } catch {
    return null
  }
}

const LiveAudioWaveform = ({
  audio,
  isPlaying
}: {
  audio: HTMLAudioElement | null
  isPlaying: boolean
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const levelsRef = useRef<number[]>(
    Array.from({ length: liveWaveformBarCount }, (_, index) => liveWaveformFallbackLevels[index % liveWaveformFallbackLevels.length] ?? 0.3)
  )
  const frequencyBinLimitRef = useRef(0)

  useEffect(() => {
    const canvas = canvasRef.current

    if (!canvas) {
      return undefined
    }

    const context = canvas.getContext('2d')

    if (!context) {
      return undefined
    }

    const analysis = audio ? getAudioAnalysis(audio) : null
    const size = {
      activeColor: getCssColor(canvas, '--primary', '#6b8db5'),
      barWidth: 2,
      gap: 4,
      height: 1,
      pixelRatio: window.devicePixelRatio || 1,
      restingColor: window.getComputedStyle(canvas).color || 'rgba(111, 121, 130, 0.5)',
      width: 1
    }
    let frameId = 0
    let lastFrameAt = 0

    const syncCanvasSize = (): void => {
      const rect = canvas.getBoundingClientRect()
      const pixelRatio = window.devicePixelRatio || 1
      const width = Math.max(1, Math.floor(rect.width * pixelRatio))
      const height = Math.max(1, Math.floor(rect.height * pixelRatio))

      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width
        canvas.height = height
      }

      size.activeColor = getCssColor(canvas, '--primary', '#6b8db5')
      size.barWidth = Math.max(2 * pixelRatio, (width - (4 * pixelRatio) * (liveWaveformBarCount - 1)) / liveWaveformBarCount)
      size.gap = 4 * pixelRatio
      size.height = height
      size.pixelRatio = pixelRatio
      size.restingColor = window.getComputedStyle(canvas).color || 'rgba(111, 121, 130, 0.5)'
      size.width = width
    }

    const draw = (): void => {
      context.clearRect(0, 0, size.width, size.height)

      if (analysis && isPlaying) {
        analysis.analyser.getByteFrequencyData(analysis.data)
      }

      let binLimit = analysis?.data.length ?? 1
      let framePeak = 0

      if (analysis && isPlaying) {
        const detectedBinLimit = getAdaptiveFrequencyBinLimit(analysis.data)
        const currentBinLimit = frequencyBinLimitRef.current || detectedBinLimit
        const nextBinLimit = currentBinLimit + (detectedBinLimit - currentBinLimit) * 0.16

        frequencyBinLimitRef.current = nextBinLimit
        binLimit = Math.max(1, Math.min(analysis.data.length, Math.round(nextBinLimit)))

        for (let bin = 1; bin < binLimit; bin += 1) {
          framePeak = Math.max(framePeak, analysis.data[bin] ?? 0)
        }
      }

      for (let index = 0; index < liveWaveformBarCount; index += 1) {
        const startRatio = index / liveWaveformBarCount
        const endRatio = (index + 1) / liveWaveformBarCount
        const binStart = Math.floor((startRatio ** 1.18) * binLimit)
        const binEnd = Math.max(binStart + 1, Math.floor((endRatio ** 1.18) * binLimit))
        let targetLevel = liveWaveformFallbackLevels[index % liveWaveformFallbackLevels.length] ?? 0.3

        if (analysis && isPlaying) {
          let sum = 0

          for (let bin = binStart; bin < binEnd; bin += 1) {
            sum += analysis.data[bin] ?? 0
          }

          const average = sum / (binEnd - binStart)
          const normalized = framePeak > 0
            ? average / Math.max(24, framePeak * 1.18)
            : 0

          targetLevel = Math.max(
            liveWaveformMinLevel,
            Math.min(liveWaveformMaxLevel, normalized ** 0.82)
          )
        }

        const currentLevel = levelsRef.current[index] ?? targetLevel
        const smoothing = targetLevel > currentLevel ? liveWaveformLevelAttack : liveWaveformLevelRelease
        const nextLevel = currentLevel + (targetLevel - currentLevel) * smoothing

        levelsRef.current[index] = nextLevel

        const barHeight = Math.max(4 * size.pixelRatio, nextLevel * size.height * 0.76)
        const x = index * (size.barWidth + size.gap)
        const y = (size.height - barHeight) / 2

        context.fillStyle = analysis && isPlaying ? size.activeColor : size.restingColor
        context.globalAlpha = analysis && isPlaying ? 0.78 : 0.32
        context.beginPath()
        context.roundRect(x, y, size.barWidth, barHeight, size.barWidth / 2)
        context.fill()
      }

      context.globalAlpha = 1
    }

    const tick = (timestamp: number): void => {
      if (timestamp - lastFrameAt >= liveWaveformFrameMs) {
        lastFrameAt = timestamp
        draw()
      }

      frameId = window.requestAnimationFrame(tick)
    }

    if (audio && isPlaying && analysis?.context.state === 'suspended') {
      void analysis.context.resume()
    }

    const resizeObserver = new ResizeObserver(() => {
      syncCanvasSize()
      draw()
    })

    syncCanvasSize()
    draw()

    if (isPlaying) {
      frameId = window.requestAnimationFrame(tick)
    }

    resizeObserver.observe(canvas)

    return () => {
      window.cancelAnimationFrame(frameId)
      resizeObserver.disconnect()
    }
  }, [audio, isPlaying])

  return <canvas className={cx('public-list-player-wave')} ref={canvasRef} />
}

type PublicChannelsPageProps = {
  auth: AuthState
}

export const PublicChannelsPage = ({ auth }: PublicChannelsPageProps) => {
  const navigate = useNavigate()
  const { toast } = useToast()
  const showPlayerToast = useCallback((message: string): void => {
    toast(message)
  }, [toast])
  const personalPlayer = useChannelAudioPlayer()
  const livePlayer = usePublicLiveStreamPlayer({ onUserNotice: showPlayerToast })
  const [channels, setChannels] = useState<PublicChannel[]>([])
  const [hoveredChannel, setHoveredChannel] = useState<PublicChannel | null>(null)
  const [playbackMode, setPlaybackMode] = useState<PublicPlaybackMode>(() => readStoredPlaybackMode())
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [listeningSignalsOpen, setListeningSignalsOpen] = useState(false)
  const [listeningSignalsLoadState, setListeningSignalsLoadState] = useState<'idle' | 'loading' | 'ready'>('idle')
  const [loadedListeningSignalContextKey, setLoadedListeningSignalContextKey] = useState<string | null>(null)
  const [savingSignal, setSavingSignal] = useState<ListeningSignalType | null>(null)
  const [selectedListeningSignals, setSelectedListeningSignals] = useState<Record<ListeningSignalCategory, ListeningSignalType | null>>(emptyListeningSignalSelection)
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const settingsRef = useRef<HTMLDivElement | null>(null)
  const listeningSignalStateVersionRef = useRef(0)
  const activeChannel = playbackMode === 'live' ? livePlayer.channel : personalPlayer.channel
  const activeTrack = playbackMode === 'live' ? livePlayer.currentTrack : personalPlayer.currentTrack
  const activeErrorMessage = playbackMode === 'live' ? livePlayer.errorMessage : personalPlayer.errorMessage
  const activeIsPlaying = playbackMode === 'live' ? livePlayer.isPlaying : personalPlayer.isPlaying
  const activeIsLoading = playbackMode === 'live' ? livePlayer.isLoading : personalPlayer.isLoadingNext
  const activeIsTakingLong = playbackMode === 'live' && livePlayer.isTakingLong
  const activeVolume = playbackMode === 'live' ? livePlayer.volume : personalPlayer.volume
  const activeHasError = Boolean(activeErrorMessage)
  const liveIsConnecting = playbackMode === 'live' && activeIsLoading && !activeHasError
  const liveIsFlac = playbackMode === 'live' && livePlayer.currentTrack?.source_quality === 'flac'
  const signalChannel = activeChannel ? channels.find((channel) => channel.slug === activeChannel.slug) ?? null : null
  const signalTrack = activeTrack
  const signalPlaybackMode = playbackMode === 'live' ? 'live_stream' : 'personal_queue'
  const ambientChannel = hoveredChannel ?? activeChannel
  const ambientTheme = ambientChannel ? getChannelColorTheme(ambientChannel.color_theme) : null
  const progress = playbackMode === 'personal' && personalPlayer.duration > 0
    ? Math.min(100, (personalPlayer.currentTime / personalPlayer.duration) * 100)
    : 0
  const playerArtworkUrl = activeTrack ? getAudioImageUrl(activeTrack, 'thumb') : null
  const playerTrackText = activeTrack
    ? `${activeTrack.artist ? `${activeTrack.artist} - ` : ''}${activeTrack.title}`
    : playbackMode === 'live'
      ? activeIsTakingLong
        ? 'Still connecting. This is taking longer than usual.'
        : liveIsConnecting
          ? 'Connecting to live stream...'
          : activeHasError
            ? 'Stream could not start.'
            : 'Waiting for current track...'
      : 'Loading track...'
  const canSignalNow = auth.state === 'authenticated'
    && Boolean(signalChannel)
    && Boolean(signalTrack)
  const currentListeningSignalContextKey = canSignalNow && signalChannel && signalTrack
    ? `${signalChannel.id}:${signalTrack.id}:${signalPlaybackMode}`
    : null
  const listeningSignalsLoading = listeningSignalsOpen
    && (
      listeningSignalsLoadState !== 'ready'
      || loadedListeningSignalContextKey !== currentListeningSignalContextKey
    )

  useEffect(() => {
    window.localStorage.setItem(playbackModeStorageKey, playbackMode)

    if (playbackMode === 'live') {
      personalPlayer.stop()
    } else {
      livePlayer.stop()
    }
  }, [livePlayer.stop, personalPlayer.stop, playbackMode])

  useEffect(() => {
    if (!settingsOpen) {
      return undefined
    }

    const closeSettings = (event: PointerEvent): void => {
      const target = event.target

      if (target instanceof Element && settingsRef.current?.contains(target)) {
        return
      }

      setSettingsOpen(false)
    }

    document.addEventListener('pointerdown', closeSettings)

    return () => {
      document.removeEventListener('pointerdown', closeSettings)
    }
  }, [settingsOpen])

  const loadChannels = async (): Promise<void> => {
    setState('loading')
    setErrorMessage(null)

    try {
      setChannels(await getPublicChannels())
      setState('ready')
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to load channels')
      setState('error')
    }
  }

  useEffect(() => {
    void loadChannels()
  }, [])

  useEffect(() => {
    listeningSignalStateVersionRef.current += 1
    setListeningSignalsLoadState('idle')
    setLoadedListeningSignalContextKey(null)
    setSelectedListeningSignals(emptyListeningSignalSelection)
    setSavingSignal(null)
  }, [signalChannel?.id, signalTrack?.id, signalPlaybackMode])

  useEffect(() => {
    if (!listeningSignalsOpen || !canSignalNow || !signalChannel || !signalTrack || !currentListeningSignalContextKey) {
      return undefined
    }

    let cancelled = false
    const stateVersion = listeningSignalStateVersionRef.current
    const contextKey = currentListeningSignalContextKey
    setListeningSignalsLoadState('loading')

    const loadCurrentListeningSignals = async (): Promise<void> => {
      try {
        const signals = await getCurrentListeningSignals({
          audioTrackId: signalTrack.id,
          channelId: signalChannel.id
        })

        if (cancelled || stateVersion !== listeningSignalStateVersionRef.current) {
          return
        }

        setSelectedListeningSignals(buildListeningSignalSelection(signals))
        setLoadedListeningSignalContextKey(contextKey)
        setListeningSignalsLoadState('ready')
      } catch (error) {
        if (!cancelled) {
          toast(error instanceof Error ? error.message : 'Unable to load saved listening signals.')
          setLoadedListeningSignalContextKey(contextKey)
          setListeningSignalsLoadState('ready')
        }
      }
    }

    void loadCurrentListeningSignals()

    return () => {
      cancelled = true
    }
  }, [canSignalNow, currentListeningSignalContextKey, listeningSignalsOpen, toast])

  const openListeningSignals = (): void => {
    setListeningSignalsLoadState('idle')
    setLoadedListeningSignalContextKey(null)
    setListeningSignalsOpen(true)
  }

  const openVisualChannel = (channel: PublicChannel): void => {
    navigate(`/channels/${channel.slug}`)
  }

  const toggleChannelAudio = (channel: PublicChannel): void => {
    if (playbackMode === 'live') {
      if (livePlayer.channel?.slug === channel.slug && livePlayer.isLoading) {
        return
      }

      void livePlayer.playChannel(channel)
      return
    }

    if (personalPlayer.channel?.slug === channel.slug && personalPlayer.isLoadingNext) {
      return
    }

    void personalPlayer.playChannel(channel)
  }

  const handleCardKeyDown = (
    event: KeyboardEvent<HTMLElement>,
    channel: PublicChannel
  ): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      toggleChannelAudio(channel)
    }
  }

  const submitListeningSignal = async (category: ListeningSignalCategory, signalType: ListeningSignalType): Promise<void> => {
    if (!signalChannel || !signalTrack || !currentListeningSignalContextKey || listeningSignalsLoading) {
      return
    }

    listeningSignalStateVersionRef.current += 1
    const stateVersion = listeningSignalStateVersionRef.current
    setSavingSignal(signalType)
    try {
      await createListeningSignal({
        audioTrackId: signalPlaybackMode === 'personal_queue' ? signalTrack.id : undefined,
        channelId: signalChannel.id,
        playbackMode: signalPlaybackMode,
        signalType
      })
      if (stateVersion !== listeningSignalStateVersionRef.current) {
        return
      }
      setSelectedListeningSignals((current) => ({
        ...current,
        [category]: signalType
      }))
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Unable to save listening signal.')
    } finally {
      setSavingSignal(null)
    }
  }

  return (
    <main
      className={cx('public-shell', activeChannel && 'has-public-list-player')}
      style={ambientTheme ? {
        '--ambient-glow': ambientTheme.ambientGlow,
        '--ambient-glow-alt': ambientTheme.ambientGlowAlt
      } as CSSProperties : undefined}
    >
      <header className={cx('public-header', settingsOpen && 'settings-open')}>
        <Link className={cx('public-brand')} to="/">
          <MavolyraLogo className={cx('brand-logo', 'public-brand-logo')} variant="public" />
        </Link>
        <div className={cx('public-header-actions')}>
          <div className={cx('public-settings')} ref={settingsRef}>
            <button
              aria-expanded={settingsOpen}
              aria-label="Playback settings"
              className={cx('public-header-button')}
              onClick={() => setSettingsOpen((current) => !current)}
              title="Playback settings"
              type="button"
            >
              <Settings size={18} />
            </button>
            {settingsOpen && (
              <div
                className={cx('public-settings-layer')}
                onMouseDown={(event) => {
                  if (event.target === event.currentTarget) {
                    setSettingsOpen(false)
                  }
                }}
              >
                <div className={cx('public-settings-popover')} role="dialog" aria-label="Playback settings" aria-modal="true">
                  <div className={cx('public-settings-title-row')}>
                    <span className={cx('public-settings-label')}>Playback mode</span>
                    <button
                      aria-label="Close playback settings"
                      className={cx('public-settings-close')}
                      onClick={() => setSettingsOpen(false)}
                      type="button"
                    >
                      <X size={17} />
                    </button>
                  </div>
                  <div className={cx('public-mode-switch')} role="group" aria-label="Playback mode">
                    <button
                      className={cx(playbackMode === 'live' && 'active')}
                      onClick={() => setPlaybackMode('live')}
                      type="button"
                    >
                      Live
                    </button>
                    <button
                      className={cx(playbackMode === 'personal' && 'active')}
                      onClick={() => setPlaybackMode('personal')}
                      type="button"
                    >
                      Personal
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <Link className={cx('public-header-button')} to="/admin/login" aria-label="Open admin login" title="Admin">
            <LockKeyhole size={18} />
          </Link>
        </div>
      </header>

      <section className={cx('public-intro')} aria-labelledby="public-intro-title">
        <span className={cx('public-eyebrow')}>Never off</span>
        <h1 id="public-intro-title">The room, retuned.</h1>
        <p>From velvet hum to neon pulse.</p>
      </section>

      {state === 'loading' && (
        <section className={cx('public-state-panel')} aria-live="polite">
          <RefreshCw className={cx('public-state-spinner')} size={22} />
          <span>Loading channels...</span>
        </section>
      )}

      {state === 'error' && (
        <section className={cx('public-state-panel', 'public-state-panel-error')} aria-live="polite">
          <span>{errorMessage}</span>
          <Button onClick={() => void loadChannels()}>
            <RefreshCw size={16} /> Retry
          </Button>
        </section>
      )}

      {state === 'ready' && channels.length === 0 && (
        <section className={cx('public-state-panel')}>
          <Radio size={22} />
          <span>No public channels are available yet.</span>
        </section>
      )}

      {state === 'ready' && channels.length > 0 && (
        <section
          className={cx(
            'public-channel-grid',
            channels.length === 1 && 'public-channel-grid-featured',
            channels.length === 2 && 'public-channel-grid-two-up',
            channels.length === 3 && 'public-channel-grid-three-up',
            channels.length === 4 && 'public-channel-grid-four-up',
            channels.length === 5 && 'public-channel-grid-five-up',
            channels.length >= 6 && 'public-channel-grid-many-up'
          )}
          aria-label="Public channels"
        >
          {channels.map((channel, index) => {
            const isCurrentChannel = activeChannel?.slug === channel.slug
            const isPlaying = isCurrentChannel && activeIsPlaying
            const isLoadingThisChannel = isCurrentChannel && activeIsLoading
            const hasReadyVisuals = channel.ready_visual_count > 0
            const coverUrl = getChannelCoverUrl(channel, 'preview')
            const theme = getChannelColorTheme(channel.color_theme)

            return (
              <article
                className={cx('public-channel-card', `public-channel-card-${(index % 6) + 1}`, isPlaying && 'is-playing')}
                key={channel.id}
                onClick={() => toggleChannelAudio(channel)}
                onKeyDown={(event) => handleCardKeyDown(event, channel)}
                onMouseEnter={() => setHoveredChannel(channel)}
                onMouseLeave={() => setHoveredChannel(null)}
                onFocus={() => setHoveredChannel(channel)}
                onBlur={() => setHoveredChannel(null)}
                aria-label={isPlaying ? `Pause ${channel.name} audio` : `Play ${channel.name} audio`}
                aria-disabled={isLoadingThisChannel}
                role="button"
                style={{
                  '--channel-tint': theme.cardTint,
                  '--channel-accent': theme.accent,
                  '--channel-glow': theme.accentGlow,
                  '--channel-glow-alt': theme.ambientGlowAlt
                } as CSSProperties}
                tabIndex={0}
              >
                <span className={cx('public-channel-cover')} aria-hidden="true">
                  {coverUrl ? (
                    <img alt="" src={resolveMediaUrl(coverUrl)} />
                  ) : (
                    <span className={cx('public-channel-cover-placeholder')}>
                      <Radio size={34} />
                    </span>
                  )}
                </span>
                <span className={cx('public-channel-copy')}>
                  <span className={cx('public-channel-kicker')}>
                    {playbackMode === 'live' ? <Radio size={13} /> : <Headphones size={13} />}
                    {playbackMode === 'live' ? 'Live stream' : 'Personal queue'}
                  </span>
                  <strong>{channel.name}</strong>
                  <span>{channel.description || 'Calm audio and visual playback.'}</span>
                </span>
                {hasReadyVisuals && (
                  <button
                    className={cx('public-channel-action')}
                    onClick={(event) => {
                      event.stopPropagation()
                      openVisualChannel(channel)
                    }}
                    type="button"
                    aria-label={`Open visuals for ${channel.name}`}
                    title="Open visuals"
                  >
                    <Video size={16} />
                  </button>
                )}
              </article>
            )
          })}
        </section>
      )}

      {activeChannel && (
        <aside className={cx('public-list-player', `public-list-player-${playbackMode}`)} aria-label="Channel audio player">
          <div className={cx('public-list-player-main')}>
            <span className={cx('public-list-player-icon')}>
              {playerArtworkUrl ? (
                <img
                  alt=""
                  src={resolveMediaUrl(playerArtworkUrl)}
                />
              ) : liveIsConnecting ? (
                <span className={cx('public-list-player-signal')} aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              ) : (
                <Headphones size={18} />
              )}
            </span>
            <span className={cx('public-list-player-copy')}>
              <span>{activeChannel.name}</span>
              <strong>{playerTrackText}</strong>
            </span>
          </div>
          <div className={cx(
            'public-list-player-progress',
            playbackMode === 'live' && 'live',
            playbackMode === 'live' && activeIsPlaying && 'is-playing',
            playbackMode === 'live' && liveIsConnecting && 'is-connecting'
          )} aria-hidden="true">
            {liveIsConnecting ? (
              <div className={cx('public-list-player-loading-wave')}>
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
            ) : playbackMode === 'live' ? (
              <LiveAudioWaveform audio={livePlayer.audioElement} isPlaying={activeIsPlaying} />
            ) : (
              <span style={{ width: `${progress}%` }} />
            )}
          </div>
          <span className={cx('public-list-player-time')}>
            {playbackMode === 'live'
              ? (
                <span className={cx('public-live-badge')}>
                  <span className={cx('public-live-badge-dot')} aria-hidden="true" />
                  Live
                  {liveIsFlac && <span className={cx('public-live-badge-hq')}>HQ</span>}
                </span>
              )
              : `${formatPlaybackTime(personalPlayer.currentTime)} / ${formatPlaybackTime(personalPlayer.duration)}`}
          </span>
          {activeErrorMessage && (
            <span className={cx('public-list-player-error')}>{activeErrorMessage}</span>
          )}
          <button
            className={cx('public-list-player-button', 'public-list-player-button-close')}
            onClick={playbackMode === 'live' ? livePlayer.stop : personalPlayer.stop}
            type="button"
            aria-label="Stop channel audio"
          >
            <X size={16} />
          </button>
          <div className={cx('public-list-player-actions')}>
            {canSignalNow && (
              <button
                className={cx('public-list-player-button', 'public-list-player-button-signal')}
                onClick={openListeningSignals}
                type="button"
                aria-label="Open listening controls"
                title="Listening controls"
              >
                <SlidersHorizontal size={16} />
              </button>
            )}
            <div className={cx('public-list-player-transport')}>
              {playbackMode === 'live' && activeHasError ? (
                <button
                  className={cx('public-list-player-button', 'public-list-player-button-retry')}
                  onClick={() => void livePlayer.retry()}
                  type="button"
                  aria-label="Retry live stream"
                >
                  <RefreshCw size={16} />
                  <span>Retry</span>
                </button>
              ) : (
                <button
                  className={cx('public-list-player-button', 'public-list-player-button-primary')}
                  onClick={() => {
                    if (playbackMode === 'live') {
                      void livePlayer.playChannel(livePlayer.channel!)
                    } else {
                      void personalPlayer.playChannel(personalPlayer.channel!)
                    }
                  }}
                  type="button"
                  disabled={activeIsLoading}
                  aria-label={activeIsLoading ? 'Connecting channel audio' : activeIsPlaying ? 'Pause channel audio' : 'Play channel audio'}
                >
                  {activeIsLoading ? (
                    <span className={cx('public-list-player-button-loader')} aria-hidden="true" />
                  ) : activeIsPlaying ? (
                    <Pause size={16} />
                  ) : (
                    <Play size={16} />
                  )}
                </button>
              )}
              {playbackMode === 'personal' && (
                <button
                  className={cx('public-list-player-button', 'public-list-player-button-secondary')}
                  onClick={() => void personalPlayer.next()}
                  type="button"
                  disabled={personalPlayer.isLoadingNext}
                  aria-label="Next track"
                >
                  <SkipForward size={16} />
                </button>
              )}
            </div>
            <PublicVolumeControl
              buttonClassName={cx('public-list-player-volume-button')}
              className={cx('public-list-player-volume-control')}
              onChange={playbackMode === 'live' ? livePlayer.setVolume : personalPlayer.setVolume}
              volume={activeVolume}
            />
          </div>
        </aside>
      )}

      {listeningSignalsOpen && canSignalNow && signalChannel && signalTrack && (
        <div
          className={cx('listening-signal-layer')}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setListeningSignalsOpen(false)
            }
          }}
        >
          <section className={cx('listening-signal-modal')} role="dialog" aria-label="Listening controls" aria-modal="true">
            <div className={cx('listening-signal-header')}>
              <div>
                <span className={cx('listening-signal-kicker')}>{signalChannel.name}</span>
                <h2>Listening controls</h2>
              </div>
              <button
                className={cx('listening-signal-close')}
                onClick={() => setListeningSignalsOpen(false)}
                type="button"
                aria-label="Close listening controls"
              >
                <X size={18} />
              </button>
            </div>

            <div className={cx('listening-signal-now-playing')}>
              <span className={cx('listening-signal-art')} aria-hidden="true">
                {playerArtworkUrl ? (
                  <img alt="" src={resolveMediaUrl(playerArtworkUrl)} />
                ) : (
                  <Music size={20} />
                )}
              </span>
              <span className={cx('listening-signal-track-meta')}>
                <strong>{signalTrack.title}</strong>
                <span>{signalTrack.artist || 'Unknown artist'}</span>
              </span>
              <span className={cx('listening-signal-bars')} aria-hidden="true">
                <span />
                <span />
                <span />
                <span />
              </span>
            </div>

            <div className={cx('listening-signal-section-label')}>
              <span>Rate this track</span>
              <span className={cx('listening-signal-section-line')} />
              <span>{listeningSignalsLoading ? 'Loading' : `${Object.values(selectedListeningSignals).filter(Boolean).length} of 3`}</span>
            </div>

            <div className={cx('listening-signal-card-stack')} aria-busy={listeningSignalsLoading}>
              {listeningSignalBlocks.map((block) => {
                const BlockIcon = block.icon
                const selectedSignal = listeningSignalsLoading ? null : selectedListeningSignals[block.category]
                const savingAction = block.actions.find((action) => action.signalType === savingSignal)
                const selectedAction = block.actions.find((action) => action.signalType === selectedSignal)
                const statusTone = savingAction?.tone ?? selectedAction?.tone

                return (
                  <section
                    className={cx('listening-signal-card', selectedSignal && 'has-value', listeningSignalsLoading && 'loading')}
                    key={block.category}
                  >
                    <div className={cx('listening-signal-card-head')}>
                      <span className={cx('listening-signal-param-icon')} aria-hidden="true">
                        <BlockIcon size={20} />
                      </span>
                      <span className={cx('listening-signal-param-copy')}>
                        <strong>{block.title}</strong>
                        <span>{block.helper}</span>
                      </span>
                      <span className={cx('listening-signal-status', statusTone && `listening-signal-status-${statusTone}`, savingAction && 'saving', listeningSignalsLoading && 'loading')}>
                        {listeningSignalsLoading ? (
                          <>
                            <span className={cx('listening-signal-status-pulse')} aria-hidden="true" />
                            Checking
                          </>
                        ) : savingAction ? (
                          <>
                            <span className={cx('listening-signal-status-pulse')} aria-hidden="true" />
                            Saving
                          </>
                        ) : selectedAction ? selectedAction.label : 'Pick'}
                      </span>
                    </div>
                    <div className={cx('listening-signal-ratings')} role="radiogroup" aria-label={`${block.title} rating`}>
                      {block.actions.map((action) => {
                        const Icon = action.icon
                        const saving = savingSignal === action.signalType
                        const selected = selectedSignal === action.signalType
                        const blockSaving = Boolean(savingAction)

                        return (
                          <button
                            aria-checked={selected}
                            aria-label={`${block.title}: ${action.label}`}
                            className={cx(
                              'listening-signal-rating',
                              `listening-signal-rating-${action.tone}`,
                              selected && 'selected',
                              saving && 'saving',
                              listeningSignalsLoading && 'loading'
                            )}
                            disabled={listeningSignalsLoading || blockSaving}
                            key={action.signalType}
                            onClick={() => void submitListeningSignal(block.category, action.signalType)}
                            role="radio"
                            type="button"
                          >
                            {saving ? <RefreshCw size={28} /> : <Icon size={28} />}
                            <span>{action.label}</span>
                          </button>
                        )
                      })}
                    </div>
                  </section>
                )
              })}
            </div>
          </section>
        </div>
      )}
    </main>
  )
}
